import _ from 'lodash'
import { Md5 as md5 } from 'ts-md5'
import { PromptDataType } from './Prompt'
import { Timer } from '../Timer'
import { ReturnedEntityState } from './ReturnedEntity'
import { ConceptProposedEntities } from './PersistModel'

export interface ProposedEntity {
	get stateKey(): string,
}

export class ProposedPrompt implements ProposedEntity {
	cid: string|null
	title: string
	dataType: PromptDataType
	constructor(cid: string|null, title: string, dataType: PromptDataType) {
		this.cid = cid
		this.title = title
		this.dataType = dataType
	}
	get stateKey() {
		const stringified = JSON.stringify([this.title, this.dataType])
		const hash = md5.hashStr(`Prompt ${stringified}`)
		return 'p' + hash
	}
	dbProposedEntityPayload() {
		return {
			StateKey: this.stateKey,
			Title: this.title,
			DataType: this.dataType,
		}
	}
}

export class ProposedResponse implements ProposedEntity {
	cid: string|null
	title: string
	constructor(cid: string|null, title: string) {
		this.cid = cid
		this.title = title
	}
	get stateKey() {
		const stringified = this.title
		const hash = md5.hashStr(`Response ${stringified}`)
		return 'r' + hash
	}
	dbProposedEntityPayload() {
		return {
			StateKey: this.stateKey,
			Title: this.title,
		}
	}
}

export class ProposedIntersection implements ProposedEntity {
	promptKey: string
	responseKey: string
	constructor(promptKey: string, responseKey: string) {
		this.promptKey = promptKey
		this.responseKey = responseKey
	}
	get stateKey() {
		const stringified = JSON.stringify([this.promptKey, this.responseKey])
		const hash = md5.hashStr(`PromptResponseIntersection ${stringified}`)
		return 'pri' + hash
	}
}

export class EntityProposals {
	prompts: {[k: string]: ProposedPrompt} = {}
	responses: {[k: string]: ProposedResponse} = {}
	intersections: {[k: string]: ProposedIntersection} = {}

	pushPrompt(prompt: ProposedPrompt): string {
		const k = prompt.stateKey
		if(!this.prompts.hasOwnProperty(k)) {
			this.prompts[k] = prompt
		}
		return k
	}

	pushResponse(response: ProposedResponse): string {
		const k = response.stateKey
		if(!this.responses.hasOwnProperty(k)) {
			this.responses[k] = response
		}
		return k
	}

	pushIntersection(intersection: ProposedIntersection): string {
		const k = intersection.stateKey
		if(!this.intersections.hasOwnProperty(k)) {
			this.intersections[k] = intersection
		}
		return k
	}

	pushConceptProposedEntities(entities: ConceptProposedEntities) {
		for(let m of entities.prompts) {
			this.pushPrompt(m)
		}
		for(let m of entities.responses) {
			this.pushResponse(m)
		}
		for(let m of entities.intersections) {
			this.pushIntersection(m)
		}
	}

	// for submission to api.Mgr_Entities
	dbProposedEntitiesPayload() {
		const timer = new Timer('[dbProposedEntitiesPayload]')
		const payload = {
			ProposedEntities: {
				Prompts: _.map(this.prompts, m => m.dbProposedEntityPayload()),
				Responses: _.map(this.responses, m => m.dbProposedEntityPayload()),
			}
		}
		timer.end()
		return payload
	}

	// for submission to api.Mgr_Intersections
	dbProposedIntersectionsPayload(branchGuid: string, returnedEntityState: ReturnedEntityState) {
		return {
			Intersect: {
				Branch_GUID: branchGuid,
				Intersection: _.map(this.intersections, m => {
					const prompt = returnedEntityState.prompt[m.promptKey]
					const response = returnedEntityState.response[m.responseKey]
					return {
						promptStateKey: m.promptKey,
						responseStateKey: m.responseKey,
						Prompt_GUID: prompt ? prompt.GUID : null,
						Response_GUID: response ? response.GUID : null,
					}
				}),
			},
		}
	}
}
